/**
 * Copyright 2023-2025 ForgeRock AS. All Rights Reserved
 *
 * Use of this code requires a commercial software license with ForgeRock AS
 * or with one of its affiliates. All use shall be exclusively subject
 * to such license between the licensee and ForgeRock AS.
 */

import { getManagedResource } from '@forgerock/platform-shared/src/api/ManagedResourceApi';
import { evaluateAuthorizationPolicy } from '@/api/PoliciesApi';
import store from '@/store';

/**
 * Admin group types
 */
export const ADMIN_GROUPS = ['super-admins', 'tenant-admins'];
export const ADMIN_EFFECTIVE_GROUPS = ['tenant-auditor', 'brand-admin'];

/**
 * Determines whether the team members group attribute indicates they are a member of the specified group
 * @param {Array} teamMemberGroups an array of team member groups
 * @param {String} group the name of the group to check for
 * @returns {Boolean} whether or not the group is present in the groups array
 */
export function adminGroupsContainsGroup(teamMemberGroups, group) {
  return teamMemberGroups && teamMemberGroups.includes(group);
}

/**
 * Determines whether the team members effectiveGroups indicates they are a member of the specified teammembergroup
 * @param {Array} effectiveGroups an array of effective team member groups
 * @param {String} referenceId the _id of the group to check for
 * @returns {Boolean} whether or not the referenceId is present in the effectiveGroups array
 */
export function adminGroupsContainsEffectiveGroup(effectiveGroups, referenceId) {
  return effectiveGroups
    && effectiveGroups.filter((groupData) => groupData._refResourceId === referenceId).length > 0;
}

/**
 * Determines the privileges of the current admin
 * Only super admins can manage other tenant admins' sign on
 * * @param {String} id teammember id
 */
export async function determineAdminPrivileges(id) {
  const { data: { groups, effectiveGroups } } = await getManagedResource('teammember', id);
  return {
    FederationAdmin: adminGroupsContainsGroup(groups, 'super-admins'),
    TenantAdmin: adminGroupsContainsGroup(groups, 'tenant-admins'),
    TenantAuditor: adminGroupsContainsEffectiveGroup(effectiveGroups, 'tenant-auditor'),
  };
}

/** Sets the user permissions based on the evaluated UI policies
 * @returns {Object} userPermissions object containing menu item and route permissions
 */
export async function setUserPermissions() {
  try {
    const menuItemsPermissionsPromise = evaluateAuthorizationPolicy('root', {
      resource: 'menuItem:',
      application: 'platformUIPolicySet',
    });
    const routePermissionsPromise = evaluateAuthorizationPolicy('root', {
      resource: 'route:',
      application: 'platformUIPolicySet',
    });
    const [menuItemsPermissions, routePermissions] = await Promise.all([
      menuItemsPermissionsPromise,
      routePermissionsPromise,
    ]);
    const userPermissions = {
      menuItems: {},
      routes: {},
    };
    menuItemsPermissions.data.forEach((item) => {
      userPermissions.menuItems[item.resource.replace('menuItem:/', '')] = item.actions;
    });
    routePermissions.data.forEach((item) => {
      userPermissions.routes[item.resource.replace('route:/', '')] = item.actions;
    });
    // commit the userPermissions to the store
    store.commit('setUserPermissions', userPermissions);
  } catch (error) {
    console.error('Error setting user permissions:', error); // eslint-disable-line no-console
  }
}
/** Sets the route permissions based on the userPermissions object
 * @param {Object} to the route being accessed
 * @param {Function} next the next function to call
 * @param {MutationObserver} observer the mutation observer to use for disabling inputs
 */
/* eslint-disable no-param-reassign */
export function setRoutePermissions(to, next, observer) {
  const { userPermissions } = store.state;
  if (userPermissions && to.name !== 'Forbidden') {
    // userPermissions are set, so we know this is not a super admin or tenant admin so we need to
    // set the route permissions based on the defined ui policies
    try {
      const routePermission = userPermissions.routes[to.name];
      const tenantAuditorRoutePermission = userPermissions.routes['*'];
      const bodyTag = document.getElementsByTagName('body')[0];
      const routeIsReadOnly = (routePermission?.READ || tenantAuditorRoutePermission?.READ) && !routePermission?.WRITE && to.name !== 'Profile';
      // disable or enable inputs based on the routeIsReadOnly value
      observer = null;
      observer = new MutationObserver(() => {
        const inputs = document.querySelectorAll('input:not([type="search"]),textarea');
        inputs.forEach((input) => {
          input.disabled = routeIsReadOnly;
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
      // add or remove the read-only class on the body tag based on the routeIsReadOnly value
      if (routeIsReadOnly) {
        bodyTag.classList.add('ping-read-only');
      } else {
        bodyTag.classList.remove('ping-read-only');
      }
      if ((to.path === '/' || to.path === '/dashboard/overview') && store.state.defaultRoute) {
        next({ name: store.state.defaultRoute.name });
      } else {
        next();
      }
    } catch (error) {
      console.error('Error setting route permissions', error); // eslint-disable-line no-console
      // If there is an error setting the route permissions then we need to redirect to the forbidden page
      // because the user does not have access to this resource
      next({ name: 'Forbidden' });
    }
  } else {
    // If there are no permissions set, set observer to null and allow access to the route
    observer = null;
    next();
  }
}
/* eslint-enable no-param-reassign */
